import { GlobalMessages } from '@shared/localization/messages';
import type { ReactNode } from 'react';
import { useState } from 'react';
import AnimateHeight from 'react-animate-height';

import IconCaretDown from '@/assets/Icons/IconCaretDown';
import IconHorizontalDots from '@/assets/Icons/IconHorizontalDots';
import IconTrashLines from '@/assets/Icons/IconTrashLines';
import { useMessages } from '@/hooks/messages';

interface AdminBlockComponentProps {
  className?: string;
  showOrder?: boolean;
  showDelete?: boolean;
  isCollapsible?: boolean;
  initCollapsed?: boolean;
  name: string | ReactNode;
  children: ReactNode[];
  onDelete?: () => void;
}

export const AdminBlockComponent = ({
  children,
  name,
  className,
  showOrder = false,
  showDelete = true,
  isCollapsible = true,
  initCollapsed = false,
  onDelete,
}: AdminBlockComponentProps) => {
  const [collapsed, setCollapsed] = useState(initCollapsed);

  return (
    <div
      className={
        className ? className : 'panel items-md-center dark:border-dark rounded-md px-6 py-3.5'
      }
    >
      <div className='flex flex-nowrap items-center justify-center'>
        <div className='min-w-0 shrink grow basis-0 overflow-x-hidden font-bold'>
          <h5 className='mb-0'>{name}</h5>
        </div>
        <AdminBlockComponentButtons
          showOrder={showOrder}
          showDelete={showDelete}
          isCollapsible={isCollapsible}
          initCollapsed={initCollapsed}
          onDelete={onDelete}
          onCollapseChange={setCollapsed}
        />
      </div>
      <AnimateHeight duration={300} height={!collapsed ? 'auto' : 0}>
        {children}
      </AnimateHeight>
    </div>
  );
};

interface AdminBlockComponentButtonsProps {
  showOrder?: boolean;
  showDelete?: boolean;
  isCollapsible?: boolean;
  initCollapsed?: boolean;
  onDelete?: () => void;
  onCollapseChange: (collapsed: boolean) => void;
}

export const AdminBlockComponentButtons = ({
  showOrder = false,
  showDelete = true,
  isCollapsible = true,
  initCollapsed = false,
  onDelete = () => {},
  onCollapseChange = () => {},
}: AdminBlockComponentButtonsProps) => {
  const [collapsed, setCollapsed] = useState(initCollapsed);

  const globalMessages = useMessages(GlobalMessages);

  function toggleCollapse() {
    setCollapsed(!collapsed);
    onCollapseChange(!collapsed);
  }

  return (
    <div className='flex flex-nowrap justify-end pl-4 text-xs'>
      {showOrder ? (
        <button
          type='button'
          className='handle btn btn-outline-secondary ml-2 h-7 w-7 cursor-move p-0.5'
        >
          <IconHorizontalDots className='m-auto' />
        </button>
      ) : null}
      {showDelete ? (
        <button
          type='button'
          onClick={onDelete}
          className='btn btn-outline-danger btn-sm ml-2 h-7 p-0.5 pr-1.5 pl-1'
        >
          <IconTrashLines className='m-auto' />
          {globalMessages.delete()}
        </button>
      ) : null}
      {isCollapsible ? (
        <button
          type='button'
          onClick={toggleCollapse}
          className={
            'btn btn-outline-secondary ml-2 h-7 w-7 p-0.5 ' + (!collapsed ? 'rotate-[-180deg]' : '')
          }
        >
          <IconCaretDown className='m-auto' />
        </button>
      ) : null}
    </div>
  );
};
